// Undocumented fields of the agent's transaction, which it builds its own traceparent from.
interface RumTransaction {
    traceId?: string
    id?: string
    sampled?: boolean
    end?: (endTime?: number) => void
}

/**
 * Wraps `fetch` so a same-origin request names the current RUM transaction as its parent (ADR 0005).
 * `ignore` matches paths whose transactions the agent ignores, so they could not parent a backend span.
 *
 * Install before the agent is imported: it wraps `fetch` on import, so its instrumentation runs
 * first and starts the http-request transaction for a request made outside one.
 */
export function propagateTraceparent(currentTransaction: () => RumTransaction | undefined, ignore?: RegExp) {
    const nativeFetch = window.fetch.bind(window)
    const endsNoSooner = new WeakMap<RumTransaction, number>()

    // The agent discards a transaction under 1 ms, and drops a fetch span cut to under 1 ms, leaving it none; 2 ms survives float rounding.
    function keepReportable(transaction: RumTransaction) {
        const end = transaction.end
        if (!end) return
        if (!endsNoSooner.has(transaction)) {
            transaction.end = endTime => end.call(transaction, Math.max(endTime ?? performance.now(), endsNoSooner.get(transaction)!))
        }
        endsNoSooner.set(transaction, performance.now() + 2)
    }

    window.fetch = (input, init) => {
        const transaction = currentTransaction()
        if (!transaction?.traceId || !transaction.id) return nativeFetch(input, init)

        const url = new URL(input instanceof Request ? input.url : input, location.href)
        if (url.origin !== location.origin || ignore?.test(url.pathname)) {
            return nativeFetch(input, init)
        }

        keepReportable(transaction)
        const request = new Request(input, init)
        request.headers.set('traceparent', `00-${transaction.traceId}-${transaction.id}-${transaction.sampled ? '01' : '00'}`)
        return nativeFetch(request)
    }
}
